import React, { useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { useParams } from "react-router-dom";
import * as S from "../../components/Modal/Modal.styles";
import * as UserStyles from "./UserDetails.styles";
import { getUser, inactivateUser, promoteUserToAdmin } from "../../api";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { getUserRole } from "../../helpers";
import { useLogoutAllDevices } from "../../hooks/useLogoutAllDevices";
import { StatusCodes } from "http-status-codes";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";
import ModalConfirm from "../../components/ModalConfirm";

const UserDetails = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [user, setUser] = useState(null);

  const { isFeatureFlagEnable: isAdminRoleFlagEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PERMISSION.ADMIN_ROLE_ASSIGNMENT
  );

  const isAdminRoleAssignmentEnabled =
    isAdminRoleFlagEnabled ||
    process.env.REACT_APP_FORCE_ADMIN_ROLE_ASSIGNMENT === "true";

  const {
    showModal: showLogoutModal,
    setShowModal: setShowLogoutModal,
    openModal,
    closeModal,
    loading: logoutLoading,
    error: logoutError,
    handleLogout: handleLogoutAllDevices,
  } = useLogoutAllDevices(id);

  const isInactivable = user?.status === "active" && user?.role !== "client" && user?.role !== "admin";

  const isPromotable =
    isAdminRoleAssignmentEnabled &&
    user?.role !== "admin" &&
    user?.status === "active";

  const clientId = user?.client_id || user?.parent_client_id;

  const formatIsoToDateBr = (isoString) => {
    if (!isoString) return "";

    try {
      return format(parseISO(isoString), "dd/MM/yyyy");
    } catch (error) {
      toast.error("Data inválida");
      return "";
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await getUser(id);
      setUser(response.data);
    } catch (error) {
      toast.error("Erro ao buscar usuário.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleInactivateUser = async () => {
    try {
      await inactivateUser(id);
    } catch (error) {
      toast.error("Erro ao inativar o usuário.");
      console.error({error});
    } finally {
      fetchUser();
      setShowConfirmModal(false);
    }
  };

  const handlePromoteUserToAdmin = async () => {
    setPromoteLoading(true);
    try {
      await promoteUserToAdmin(id);
      toast.success("Usuário promovido a administrador com sucesso!");
      setShowPromoteModal(false);
      await fetchUser();
    } catch (error) {
      const status = error?.response?.status;

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          toast.error("Você não tem permissão para realizar esta ação.");
          break;
        case StatusCodes.FORBIDDEN:
          toast.error("Não é permitido promover a si mesmo.");
          break;
        case StatusCodes.NOT_FOUND:
          toast.error("Funcionalidade indisponível ou usuário não encontrado.");
          break;
        case StatusCodes.CONFLICT:
          toast.error("Usuário já é administrador ou não é elegível para promoção.");
          break;
        default:
          toast.error("Não foi possível promover o usuário. Tente novamente.");
      }

      setShowPromoteModal(false);
    } finally {
      setPromoteLoading(false);
    }
  };

  const onNavigateToClient = () => {
    if (clientId) {
      window.open(`/clientes/${clientId}`, "_blank");
    }
  };

  const renderUserInfo = () => {
    if (!user) return null;

    return (
      <UserStyles.Container>
        <UserStyles.InfoRow>
          <UserStyles.Label>ID:</UserStyles.Label>
          <UserStyles.Value>{user.id}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Nome:</UserStyles.Label>
          <UserStyles.Value>{user.name}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Função:</UserStyles.Label>
          <UserStyles.Value>{getUserRole(user.role)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Email:</UserStyles.Label>
          <UserStyles.Value>{user.email}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Status de verificação:</UserStyles.Label>
          <UserStyles.Value>{user.verified ? "Verificado" : "Não verificado"}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Data de criação:</UserStyles.Label>
          <UserStyles.Value>{formatIsoToDateBr(user.created_at)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Última modificação:</UserStyles.Label>
          <UserStyles.Value>{formatIsoToDateBr(user.modified_at)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Fonte de registro:</UserStyles.Label>
          <UserStyles.Value>{user.registration_source}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Cidade de referência:</UserStyles.Label>
          <UserStyles.Value>{user.reference_city ? user.reference_city : "Não informado"}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Permissão para usar JusDecision:</UserStyles.Label>
          <UserStyles.Value>{user.may_use_jusdecision ? "Permitido" : "Não permitido"}</UserStyles.Value>
        </UserStyles.InfoRow>

        <UserStyles.ButtonWrapper>
          {clientId ?  
            <UserStyles.Button onClick={onNavigateToClient}>
              Acessar Cliente
            </UserStyles.Button>
            : null}

          <UserStyles.Button color="secondary" onClick={openModal}>
            Deslogar de Todos Dispositivos
          </UserStyles.Button>

          {isInactivable &&
            <UserStyles.Button color="secondary" onClick={() => setShowConfirmModal(true)}>
              Inativar Usuário
            </UserStyles.Button>
          }

          {isPromotable &&
            <UserStyles.Button onClick={() => setShowPromoteModal(true)}>
              Promover a Administrador
            </UserStyles.Button>
          }
        </UserStyles.ButtonWrapper>
      </UserStyles.Container>
    );
  };

  return (
    <BlockUi tag="div" blocking={loading}>
      {user && (
        <UserStyles.Card>
          <UserStyles.Header>Detalhes do Usuário</UserStyles.Header>
          {renderUserInfo()}
        </UserStyles.Card>
      )}

      {showConfirmModal && (
        <S.Modal>
          <S.ModalContent>
            <S.ModalTitle>Confirmação</S.ModalTitle>
            <S.Text>Tem certeza que deseja inativar este usuário?</S.Text>
            <S.ButtonRow>
              <S.ConfirmButton onClick={handleInactivateUser}>
                Confirmar
              </S.ConfirmButton>
              <S.CancelButton onClick={() => setShowConfirmModal(false)}>
                Cancelar
              </S.CancelButton>
            </S.ButtonRow>
          </S.ModalContent>
        </S.Modal>
      )}

      {showPromoteModal && (
        <ModalConfirm
          title="Confirmação"
          message="Tem certeza que deseja promover este usuário a administrador?"
          onConfirm={handlePromoteUserToAdmin}
          onClose={() => setShowPromoteModal(false)}
          isLoading={promoteLoading}
        />
      )}

      {showLogoutModal && (
        <S.Modal>
          <S.ModalContent>
            <S.ModalTitle>Confirmação</S.ModalTitle>
            <S.Text>Tem certeza que deseja deslogar este usuário de todos os dispositivos?</S.Text>
            {logoutError && (
              <div style={{ color: 'red', marginTop: '10px', marginBottom: '10px' }}>
                {logoutError}
              </div>
            )}
            <S.ButtonRow>
              <S.ConfirmButton 
                onClick={handleLogoutAllDevices}
                disabled={logoutLoading}
              >
                {logoutLoading ? "Processando..." : "Confirmar"}
              </S.ConfirmButton>
              <S.CancelButton 
                onClick={closeModal}
                disabled={logoutLoading}
              >
                Cancelar
              </S.CancelButton>
            </S.ButtonRow>
          </S.ModalContent>
        </S.Modal>
      )}
    </BlockUi>
  );
};

export default UserDetails;
